import { useCallback, useState } from "react";
import CONFIG from "../../config/config";
import { apiFetch } from "../../services/http";
import { AD_DOWNLOAD_ERROR_MESSAGE } from "../../utils/errorMessages";

/**
 * Downloads an advertisement's original media file.
 *
 * Uses the header-authenticated `/download-url` endpoint to obtain a pre-signed
 * S3 URL carrying `Content-Disposition: attachment`, then triggers it through a
 * synthetic anchor. The bytes stream browser <-> S3 directly, so large videos
 * never pass through the API server.
 *
 * @returns {{ download: (ad: object) => Promise<void>, downloadingId: string|null, error: string|null, clearError: () => void }}
 */
const useAdDownload = () => {
  const [downloadingId, setDownloadingId] = useState(null);
  const [error, setError] = useState(null);

  const clearError = useCallback(() => setError(null), []);

  const download = useCallback(async (ad) => {
    if (!ad?._id) return;
    setDownloadingId(ad._id);
    setError(null);
    try {
      const token = localStorage.getItem("portalToken");
      const res = await apiFetch(
        `${CONFIG.BASE_URL}/api/portal/advertisements/${ad._id}/download-url`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to prepare download");
      }
      const { url, fileName } = await res.json();

      const a = document.createElement("a");
      a.href = url;
      // S3 already sets the attachment disposition; this is a belt-and-braces
      // hint for the filename and is ignored for cross-origin URLs.
      a.download = fileName || ad.originalFileName || "advertisement";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      setError(AD_DOWNLOAD_ERROR_MESSAGE);
    } finally {
      setDownloadingId(null);
    }
  }, []);

  return { download, downloadingId, error, clearError };
};

export default useAdDownload;
